import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const TABS = ['Items', 'Categories'];

const CONDITION_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
  { value: 'damaged', label: 'Damaged' },
];

const CONDITION_COLORS = {
  new: 'bg-green-100 text-green-700',
  good: 'bg-blue-100 text-blue-700',
  fair: 'bg-yellow-100 text-yellow-700',
  poor: 'bg-orange-100 text-orange-700',
  damaged: 'bg-red-100 text-red-700',
};

function FormInput({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
      <input {...props} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
    </div>
  );
}

function FormSelect({ label, options, placeholder, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
      <select {...props} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export default function AdminInventory() {
  const [tab, setTab] = useState('Items');
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(() => {
    api.get('/admin/inventory/stats/').then(r => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Inventory Management</h1>
      <p className="text-sm text-gray-500 mb-6">Manage inventory items and categories</p>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500 font-semibold uppercase">Total Items</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total_items}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500 font-semibold uppercase">Categories</p>
            <p className="text-2xl font-bold text-blue-600">{stats.total_categories}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500 font-semibold uppercase">Total Quantity</p>
            <p className="text-2xl font-bold text-green-600">{stats.total_quantity}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500 font-semibold uppercase">Conditions</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {(stats.by_condition || []).map(c => (
                <span key={c.condition} className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${CONDITION_COLORS[c.condition] || 'bg-gray-100 text-gray-600'}`}>
                  {c.condition}: {c.count}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${
              tab === t ? 'bg-white text-[#0B5D1E] shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>{t}</button>
        ))}
      </div>

      {tab === 'Items' && <ItemsTab onRefreshStats={fetchStats} />}
      {tab === 'Categories' && <CategoriesTab onRefreshStats={fetchStats} />}
    </div>
  );
}

// ═══ ITEMS TAB ═══
function ItemsTab({ onRefreshStats }) {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [conditionFilter, setConditionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [form, setForm] = useState({
    name: '', description: '', category: '', quantity: 1, unit: '',
    condition: 'new', location: '', serial_number: '', purchase_date: '',
    purchase_price: '', notes: '',
  });

  const fetchItems = useCallback(() => {
    setLoading(true);
    let url = '/admin/inventory/items/?';
    if (search) url += `search=${encodeURIComponent(search)}&`;
    if (conditionFilter) url += `condition=${conditionFilter}&`;
    api.get(url)
      .then(r => setItems(r.data.results || r.data))
      .catch(() => toast.error('Failed to load items'))
      .finally(() => setLoading(false));
  }, [search, conditionFilter]);

  const fetchCategories = useCallback(() => {
    api.get('/admin/inventory/categories/').then(r => setCategories(r.data.results || r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const resetForm = () => {
    setForm({ name: '', description: '', category: '', quantity: 1, unit: '', condition: 'new', location: '', serial_number: '', purchase_date: '', purchase_price: '', notes: '' });
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (item) => {
    setForm({
      name: item.name, description: item.description || '', category: item.category || '',
      quantity: item.quantity, unit: item.unit || '', condition: item.condition,
      location: item.location || '', serial_number: item.serial_number || '',
      purchase_date: item.purchase_date || '', purchase_price: item.purchase_price || '',
      notes: item.notes || '',
    });
    setEditing(item);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.category) delete payload.category;
    if (!payload.purchase_date) delete payload.purchase_date;
    if (!payload.purchase_price) delete payload.purchase_price;
    try {
      if (editing) {
        await api.patch(`/admin/inventory/items/${editing.id}/`, payload);
        toast.success('Item updated');
      } else {
        await api.post('/admin/inventory/items/', payload);
        toast.success('Item created');
      }
      resetForm();
      fetchItems();
      onRefreshStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save item');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inventory item?')) return;
    try {
      await api.delete(`/admin/inventory/items/${id}/`);
      toast.success('Item deleted');
      fetchItems();
      onRefreshStats();
    } catch { toast.error('Failed to delete item'); }
  };

  // Client-side date range filtering
  const filteredItems = items.filter((item) => {
    if (dateFrom && item.purchase_date && item.purchase_date < dateFrom) return false;
    if (dateTo && item.purchase_date && item.purchase_date > dateTo) return false;
    return true;
  });

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Inventory Report', 14, 18);
    doc.setFontSize(10);
    const subtitle = [
      dateFrom && dateTo ? `${dateFrom} to ${dateTo}` : dateFrom ? `From ${dateFrom}` : dateTo ? `Up to ${dateTo}` : 'All dates',
      conditionFilter ? conditionFilter.charAt(0).toUpperCase() + conditionFilter.slice(1) : 'All conditions',
    ].join(' | ');
    doc.text(subtitle, 14, 26);

    autoTable(doc, {
      startY: 32,
      head: [['S/N', 'Name', 'Category', 'Qty', 'Condition', 'Location', 'Serial #', 'Purchase Date', 'Price']],
      body: filteredItems.map((item, idx) => [
        idx + 1,
        item.name,
        item.category_name || '—',
        `${item.quantity}${item.unit ? ` ${item.unit}` : ''}`,
        item.condition_display || item.condition,
        item.location || '—',
        item.serial_number || '—',
        item.purchase_date || '—',
        item.purchase_price ? `₦${Number(item.purchase_price).toLocaleString()}` : '—',
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [11, 93, 30] },
    });

    doc.save('inventory_report.pdf');
  };

  const exportExcel = () => {
    const data = filteredItems.map((item, idx) => ({
      'S/N': idx + 1,
      Name: item.name,
      Category: item.category_name || '',
      Quantity: item.quantity,
      Unit: item.unit || '',
      Condition: item.condition_display || item.condition,
      Location: item.location || '',
      'Serial #': item.serial_number || '',
      'Purchase Date': item.purchase_date || '',
      'Purchase Price': item.purchase_price ? Number(item.purchase_price) : '',
      Description: item.description || '',
      Notes: item.notes || '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory');
    XLSX.writeFile(wb, 'inventory_report.xlsx');
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2">
        <div className="relative flex-1">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" placeholder="Search items..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E]" />
        </div>
        <select value={conditionFilter} onChange={e => setConditionFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E]">
          <option value="">All Conditions</option>
          {CONDITION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {canAdd(PERMISSIONS.ADD_INVENTORY) && (
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition whitespace-nowrap">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Add Item
          </button>
        )}
      </div>

      {/* Date range & export */}
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Purchase Date From</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">To</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
        </div>
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="px-3 py-2 text-sm text-gray-600 hover:text-red-600 border rounded-lg">Clear</button>
        )}
        <div className="ml-auto flex gap-2">
          <button onClick={downloadPDF}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a2 2 0 002 2h14a2 2 0 002-2v-3" /></svg>
            PDF
          </button>
          <button onClick={exportExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a2 2 0 002 2h14a2 2 0 002-2v-3" /></svg>
            Excel
          </button>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border p-6 mb-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">{editing ? 'Edit Item' : 'New Item'}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormInput label="Name *" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            <FormSelect label="Category" placeholder="-- Select --"
              options={categories.map(c => ({ value: c.id, label: c.name }))}
              value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} />
            <FormSelect label="Condition" options={CONDITION_OPTIONS}
              value={form.condition} onChange={e => setForm({ ...form, condition: e.target.value })} />
            <FormInput label="Quantity" type="number" min="0" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} />
            <FormInput label="Unit" placeholder="e.g. pcs, boxes" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} />
            <FormInput label="Serial Number" value={form.serial_number} onChange={e => setForm({ ...form, serial_number: e.target.value })} />
            <FormInput label="Location" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
            <FormInput label="Purchase Date" type="date" value={form.purchase_date} onChange={e => setForm({ ...form, purchase_date: e.target.value })} />
            <FormInput label="Purchase Price" type="number" step="0.01" value={form.purchase_price} onChange={e => setForm({ ...form, purchase_price: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
            <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
          </div>
          <div className="flex gap-3">
            <button type="submit" className="px-6 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition">
              {editing ? 'Update' : 'Create'}
            </button>
            <button type="button" onClick={resetForm} className="px-6 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-200 transition">Cancel</button>
          </div>
        </form>
      )}

      {/* Table */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#0B5D1E] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <h2 className="text-lg font-semibold text-gray-500 mb-2">No Items</h2>
          <p className="text-gray-400">Add your first inventory item.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">S/N</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Category</th>
                <th className="px-4 py-3 text-center">Qty</th>
                <th className="px-4 py-3 text-left">Condition</th>
                <th className="px-4 py-3 text-left">Location</th>
                <th className="px-4 py-3 text-left">Serial #</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredItems.map((item, idx) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-500">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">{item.name}</td>
                  <td className="px-4 py-3 text-gray-600">{item.category_name || '—'}</td>
                  <td className="px-4 py-3 text-center">{item.quantity}{item.unit ? ` ${item.unit}` : ''}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${CONDITION_COLORS[item.condition] || 'bg-gray-100 text-gray-600'}`}>
                      {item.condition_display || item.condition}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{item.location || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{item.serial_number || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {canEdit(PERMISSIONS.EDIT_INVENTORY) && (
                      <button onClick={() => handleEdit(item)} className="text-blue-600 hover:underline text-xs mr-3">Edit</button>
                    )}
                    {canDelete(PERMISSIONS.DELETE_INVENTORY) && (
                      <button onClick={() => handleDelete(item.id)} className="text-red-500 hover:underline text-xs">Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══ CATEGORIES TAB ═══
function CategoriesTab({ onRefreshStats }) {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ name: '', description: '' });

  const fetchCategories = useCallback(() => {
    setLoading(true);
    let url = '/admin/inventory/categories/?';
    if (search) url += `search=${encodeURIComponent(search)}&`;
    api.get(url)
      .then(r => setCategories(r.data.results || r.data))
      .catch(() => toast.error('Failed to load categories'))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const resetForm = () => { setForm({ name: '', description: '' }); setEditing(null); setShowForm(false); };

  const handleEdit = (cat) => {
    setForm({ name: cat.name, description: cat.description || '' });
    setEditing(cat);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/admin/inventory/categories/${editing.id}/`, form);
        toast.success('Category updated');
      } else {
        await api.post('/admin/inventory/categories/', form);
        toast.success('Category created');
      }
      resetForm();
      fetchCategories();
      onRefreshStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.name?.[0] || 'Failed to save category');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category? Items in this category will not be deleted.')) return;
    try {
      await api.delete(`/admin/inventory/categories/${id}/`);
      toast.success('Category deleted');
      fetchCategories();
      onRefreshStats();
    } catch { toast.error('Failed to delete category'); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" placeholder="Search categories..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E]" />
        </div>
        {canAdd(PERMISSIONS.ADD_INVENTORY) && (
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition whitespace-nowrap">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Add Category
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border p-6 mb-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">{editing ? 'Edit Category' : 'New Category'}</h3>
          <FormInput label="Name *" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
            <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
          </div>
          <div className="flex gap-3">
            <button type="submit" className="px-6 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition">
              {editing ? 'Update' : 'Create'}
            </button>
            <button type="button" onClick={resetForm} className="px-6 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-200 transition">Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#0B5D1E] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      ) : categories.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <h2 className="text-lg font-semibold text-gray-500 mb-2">No Categories</h2>
          <p className="text-gray-400">Add your first inventory category.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">S/N</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-center">Items</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {categories.map((cat, idx) => (
                <tr key={cat.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-500">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">{cat.name}</td>
                  <td className="px-4 py-3 text-gray-600">{cat.description || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="bg-[#0B5D1E] text-white text-xs px-2 py-0.5 rounded-full">{cat.item_count}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canEdit(PERMISSIONS.EDIT_INVENTORY) && (
                      <button onClick={() => handleEdit(cat)} className="text-blue-600 hover:underline text-xs mr-3">Edit</button>
                    )}
                    {canDelete(PERMISSIONS.DELETE_INVENTORY) && (
                      <button onClick={() => handleDelete(cat.id)} className="text-red-500 hover:underline text-xs">Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
